// Shop đang mở có dùng được Gom Đơn không: hỏi GET /gomdon/api/me (spec 5.4). Lỗi bất kỳ — engine tắt, shop ngoài
// GOMDON_ALLOWED_SHOPS (403 not_enabled), nhân viên thiếu quyền (403 no_access) — đều chỉ là `enabled: false`, im
// lặng, không báo ra màn hình: menu Gom Đơn ẩn, mọi thứ khác của ShopTrack chạy như cũ.
//
// Module này nằm trong gói chính (khung menu dùng): chỉ được import client.ts, KHÔNG import socket.ts / trang.

import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth'
import { gomdon, GomdonError, meRetryInterval } from './client'
import type { GomdonMe } from './types'

/** Khoá react-query của Gom Đơn — kèm đơn vị đang mở (auth.ts cũng xoá cả cache khi đổi đơn vị / phiên) */
export const gomdonKeys = {
  me: (unit: string | null) => ['gomdon', 'me', unit] as const,
  meta: (unit: string | null) => ['gomdon', 'meta', unit] as const,
  channels: (unit: string | null) => ['gomdon', 'channels', unit] as const,
  /** Toàn bộ hội thoại (tối đa 300 mới nhất, cả tin nhắn lẫn bình luận) — trang Gom Đơn và badge menu dùng chung */
  conversations: (unit: string | null) => ['gomdon', 'conversations', unit] as const,
  conversation: (unit: string | null, id: number) => ['gomdon', 'conversation', unit, id] as const,
}

function isMe(v: unknown): v is GomdonMe {
  const m = v as GomdonMe | null
  return !!m && typeof m === 'object' && !!m.shop && typeof m.shop === 'object' && (m.role === 'owner' || m.role === 'staff')
}

export interface GomdonAccess {
  /** Hiện menu / mở trang Gom Đơn được */
  enabled: boolean
  me: GomdonMe | null
  /** Chủ shop (kết nối, gỡ kênh); nhân viên chỉ xem */
  isOwner: boolean
  /** Shop reviewer Meta: ẩn Zalo cá nhân */
  reviewMode: boolean
  /** Đang hỏi lần đầu, chưa có câu trả lời */
  loading: boolean
  /** Đang hỏi (kể cả hỏi lại sau lỗi) */
  fetching: boolean
  /** Lý do chưa dùng được (null khi enabled hoặc chưa hỏi) */
  error: GomdonError | null
  refetch: () => void
}

export function useGomdonAccess(): GomdonAccess {
  const s = useAuth()
  const unit = s.activeShopId
  // Token chỉ dùng được với engine sau bước mã email (twoFa) và khi không còn mật khẩu tạm
  const ready = s.isAuthenticated && s.is2FaVerified && !s.mustChangePassword && !!unit
  const q = useQuery({
    queryKey: gomdonKeys.me(unit),
    queryFn: async () => {
      const me = await gomdon.get<unknown>('/me')
      if (!isMe(me)) throw new GomdonError('Gom Đơn trả về dữ liệu không đọc được', 200, null)
      return me
    },
    enabled: ready,
    // Quyền đổi hiếm (danh sách shop do quản trị đặt): 5 phút mới hỏi lại khi có trang Gom Đơn mở ra.
    // Không thử lại, không hỏi lại mỗi lần quay về tab — shop chưa mở Gom Đơn chỉ tốn một yêu cầu mỗi lần tải web.
    // retryOnMount: false — lỗi rồi thì component mới gắn vào KHÔNG tự hỏi lại (chưa từng có dữ liệu thì mỗi lần hỏi lại
    // query quay về "đang tải", trang đổi qua lại giữa khung chờ và khung lỗi → vòng lặp yêu cầu). Hỏi lại bằng refetch().
    staleTime: 5 * 60_000,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    // lỗi tạm thời (engine tắt / 5xx / mất mạng) không được nằm trong cache suốt phiên của tab: 5 phút hỏi lại, menu tự hiện lại
    // khi engine lên. 401 / 403 thì không (meRetryInterval)
    refetchInterval: (query) => meRetryInterval(query.state.error),
  })
  const me = ready && q.isSuccess ? q.data : null
  const error = q.error instanceof GomdonError ? q.error : q.error ? new GomdonError(q.error.message, 0, null) : null
  return {
    enabled: !!me,
    me,
    isOwner: me?.role === 'owner',
    reviewMode: me?.reviewMode === true,
    loading: ready && q.isLoading,
    fetching: ready && q.isFetching,
    error: me ? null : error,
    refetch: () => void q.refetch(),
  }
}
